import ArrowLeft from '@assets/icons/arrow-left.svg';
import ChevronLeft from '@assets/icons/chevron-left.svg';
import React, { useMemo } from 'react';
import Reanimated from 'react-native-reanimated';
import { BlurView } from 'expo-blur';
import { Button } from './button';
import { createStyles, Theme } from '@/contexts/theme-context';
import {
  FONTS,
  HEADER_PADDING_HORIZONTAL_LARGE,
  HEADER_PADDING_HORIZONTAL_SMALL,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import {
  LayoutChangeEvent,
  StyleProp,
  Text,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewStyle,
} from 'react-native';
import { SvgProps } from 'react-native-svg';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const createHeaderShadow = (theme: Theme) =>
  shadow({
    color: theme.colors.black,
    elevation: 2,
    opacity: 0.1,
    radius: 4,
    x: 0,
    y: 4,
  });

export const ICON_MARGIN = 16;
export const ICON_WIDTH = 24;

export const ICON_BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export type Action =
  | (TouchableOpacityProps & {
      Icon: React.ComponentType<SvgProps>;
      iconProps?: SvgProps;
      isIconFillEmpty?: boolean;
    })
  | {
      component: React.ReactElement;
    };

export interface Props {
  actions?: Action[];
  after?: React.ReactNode;
  afterContainerStyle?: React.ComponentProps<typeof Reanimated.View>['style'];
  backButtonColor?: string;
  BackButtonIcon?: React.ComponentType<SvgProps>;
  backButtonLabel?: string;
  backButtonTestID?: string;
  centerTitle?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  content?: React.ReactNode;
  contentContainerStyle?: React.ComponentProps<typeof Reanimated.View>['style'];
  contentFooter?: React.ReactNode;
  contentHeader?: React.ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
  hideHeader?: boolean;
  inlineBackButton?: boolean;
  isBlurred?: boolean;
  onBackPress?: () => void;
  onContentLayout?: (e: LayoutChangeEvent) => void;
  onHeaderPress?: () => void;
  responsive?: boolean;
  showBackButton?: boolean;
  showShadow?: boolean;
  subheader?: React.ReactNode;
  title?: React.ReactNode;
}

export const CONTENT_HEIGHT = 48;
export const CONTENT_MARGIN_VERTICAL = 4;
export const CONTENT_PADDING_TOP = 20;

/**
 * Only wraps children in a TouchableOpacity if onPress is provided.
 */
interface MaybeTouchableProps {
  children: React.ReactNode;
  onPress?: () => void;
}

const MaybeTouchable: React.FC<MaybeTouchableProps> = ({
  onPress,
  children,
}) => {
  return onPress ? (
    <TouchableOpacity
      accessibilityRole="header"
      activeOpacity={1}
      onPress={onPress}
    >
      {children}
    </TouchableOpacity>
  ) : (
    <>{children}</>
  );
};

/**
 * Only wraps children in a BlurView if isBlurred is true.
 */
interface MaybeBlurProps {
  blurProps?: React.ComponentProps<typeof BlurView>;
  children: React.ReactNode;
  isBlurred?: boolean;
}
const MaybeBlur: React.FC<MaybeBlurProps> = ({
  isBlurred,
  blurProps,
  children,
}) => {
  const { top } = useSafeAreaInsets();
  return isBlurred ? (
    <BlurView
      experimentalBlurMethod="dimezisBlurView"
      intensity={50}
      style={{ paddingTop: top }}
      tint="default"
      {...blurProps}
    >
      {children}
    </BlurView>
  ) : (
    <>{children}</>
  );
};

export const NavigationHeaderContent: React.FC<Props> = ({
  actions = [],
  after,
  afterContainerStyle,
  BackButtonIcon = ArrowLeft,
  backButtonColor,
  backButtonLabel,
  backButtonTestID = 'back-icon',
  centerTitle = false,
  containerStyle,
  content,
  contentContainerStyle,
  contentFooter,
  contentHeader,
  contentStyle,
  hideHeader,
  inlineBackButton = false,
  isBlurred = false,
  onBackPress,
  onContentLayout,
  onHeaderPress,
  responsive = false,
  showBackButton,
  showShadow = true,
  subheader,
  title,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { top } = useSafeAreaInsets();
  const [styles, theme] = useStyles();

  const responsiveContainerStyle = useSmallLayoutSizeValue<
    StyleProp<ViewStyle>
  >(styles.container, [styles.container, styles.containerLarge], responsive);

  const responsiveContentContainerStyle = useSmallLayoutSizeValue<
    StyleProp<ViewStyle>
  >(
    styles.contentContainer,
    [styles.contentContainer, styles.contentContainerLarge],
    responsive,
  );

  const responsiveContentStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.content,
    [styles.content, styles.contentLarge],
    responsive,
  );

  const titleStyle = useSmallLayoutSizeValue(
    centerTitle ? TYPOGRAPHY.navigationCenterTitle : TYPOGRAPHY.navigationTitle,
    TYPOGRAPHY.navigationTitleLarge,
    responsive,
  );

  const titleContent =
    typeof title === 'string' ? (
      <Text numberOfLines={1} style={titleStyle}>
        {title}
      </Text>
    ) : (
      title || null
    );

  const hasInlineBackButton = Boolean(backButtonLabel && inlineBackButton);
  const titleContainerCenterStyle = useMemo(() => {
    // Always account for at least one action, either the back button on the
    // left or action buttons on the right
    const minActionCount = hasInlineBackButton ? 2 : 1;
    const actionCount = Math.max(actions.length, minActionCount);
    const titleMargin = (ICON_MARGIN + ICON_WIDTH) * actionCount;
    return [
      styles.titleContainerCenter,
      { left: titleMargin, right: titleMargin },
    ];
  }, [actions, hasInlineBackButton, styles]);

  const willShowShadow = showShadow && isSmallLayout;
  const shadowAsBorder = showShadow && !isSmallLayout;

  return (
    <View style={styles.base}>
      {after ? (
        <Reanimated.View
          // Set to box-none so only children receive touches and not the
          // container itself
          pointerEvents="box-none"
          // Ensures the opacity animation also applies to the view elevation
          // https://github.com/facebook/react-native/issues/23090
          renderToHardwareTextureAndroid
          style={afterContainerStyle}
        >
          {after}
        </Reanimated.View>
      ) : null}
      <View
        style={[
          { paddingTop: top },
          responsiveContainerStyle,
          willShowShadow ? styles.containerWithShadow : null,
          containerStyle,
          isBlurred ? styles.blur : null,
        ]}
      >
        <Reanimated.View
          onLayout={onContentLayout}
          style={[
            responsiveContentContainerStyle,
            contentContainerStyle,
            isBlurred ? styles.contentContainerReset : null,
          ]}
        >
          <MaybeTouchable onPress={onHeaderPress}>
            <MaybeBlur isBlurred={isBlurred}>
              {hideHeader ? null : content ? (
                content
              ) : (
                <>
                  {contentHeader ? (
                    contentHeader
                  ) : backButtonLabel && !inlineBackButton ? (
                    <View style={styles.contentHeaderBackButtonContainer}>
                      <Button
                        leftElement={
                          <ChevronLeft
                            fill={theme.colors.accent}
                            height={12}
                            width={9}
                          />
                        }
                        leftElementStyle={styles.contentHeaderBackButtonArrow}
                        onPress={() => onBackPress?.()}
                        size="small"
                        style={styles.contentHeaderBackButton}
                        textStyle={[
                          TYPOGRAPHY.small,
                          styles.contentHeaderBackButtonText,
                        ]}
                        trackingData={{
                          name: 'nav_header.back_button',
                        }}
                        type="tertiary"
                      >
                        {backButtonLabel}
                      </Button>
                    </View>
                  ) : null}
                  <View style={[responsiveContentStyle, contentStyle]}>
                    {centerTitle ? (
                      <View style={titleContainerCenterStyle}>
                        {titleContent}
                      </View>
                    ) : null}
                    <View
                      style={[styles.headerSection, styles.headerSectionLeft]}
                    >
                      {showBackButton ? (
                        <TouchableOpacity
                          hitSlop={ICON_BUTTON_HIT_SLOP}
                          onPress={onBackPress}
                          style={[
                            styles.backButton,
                            isBlurred ? styles.backButtonBlur : null,
                          ]}
                          testID={backButtonTestID}
                        >
                          {backButtonLabel ? (
                            <Text
                              style={[TYPOGRAPHY.medium, FONTS.primaryBold]}
                            >
                              {backButtonLabel}
                            </Text>
                          ) : (
                            <BackButtonIcon
                              fill={backButtonColor || theme.colors.primary}
                            />
                          )}
                        </TouchableOpacity>
                      ) : null}
                      {centerTitle ? null : titleContent}
                    </View>
                    <View style={styles.headerSection}>
                      {actions.map((action, index) => {
                        if ('component' in action) {
                          return (
                            <React.Fragment key={index}>
                              {action.component}
                            </React.Fragment>
                          );
                        }

                        const {
                          Icon,
                          iconProps = {},
                          isIconFillEmpty,
                          ...touchableOpacityProps
                        } = action;
                        if (!isIconFillEmpty) {
                          iconProps.fill =
                            iconProps.fill ?? theme.colors.primary;
                        }
                        return (
                          <TouchableOpacity
                            hitSlop={ICON_BUTTON_HIT_SLOP}
                            key={index}
                            style={styles.iconButton}
                            {...touchableOpacityProps}
                          >
                            <Icon {...iconProps} />
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                  {contentFooter}
                </>
              )}
            </MaybeBlur>
          </MaybeTouchable>
        </Reanimated.View>
        <View style={shadowAsBorder ? styles.subheaderBorder : null}>
          {subheader}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  backButton: {
    marginRight: 12,
  },
  backButtonBlur: {
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
  },
  base: {
    flexDirection: 'column-reverse',
  },
  blur: {
    backgroundColor: 'transparent',
    left: 0,
    paddingTop: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  container: {
    backgroundColor: theme.colors.white,
  },
  containerLarge: {
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_LARGE,
    paddingTop: CONTENT_PADDING_TOP,
  },
  containerWithShadow: {
    ...createHeaderShadow(theme),
  },
  content: {
    alignItems: 'stretch',
    flexDirection: 'row',
    height: CONTENT_HEIGHT,
    justifyContent: 'space-between',
    marginBottom: CONTENT_MARGIN_VERTICAL,
  },
  contentContainer: {
    overflow: 'hidden',
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
  },
  contentContainerLarge: {
    paddingHorizontal: 0,
  },
  contentContainerReset: {
    paddingHorizontal: 0,
  },
  contentHeaderBackButton: {
    borderWidth: 0,
    height: 20,
    paddingHorizontal: 0,
  },
  contentHeaderBackButtonArrow: {
    marginRight: 8,
  },
  contentHeaderBackButtonContainer: {
    alignItems: 'flex-start',
  },
  contentHeaderBackButtonText: {
    ...FONTS.primaryBold,
    color: theme.colors.accent,
  },
  contentLarge: {
    height: 56,
    marginBottom: 8,
    paddingHorizontal: 0,
  },
  headerSection: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  headerSectionLeft: {
    flex: 1,
    overflow: 'hidden',
  },
  iconButton: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: ICON_MARGIN,
    width: ICON_WIDTH,
  },
  subheaderBorder: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
  },
  titleContainerCenter: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    // apply z-index to center title so any touchables can be pressed
    // https://stackoverflow.com/questions/36938742/touchablehighlight-not-clickable-if-position-absolute
    zIndex: 1,
  },
}));
